import { type CommentItem, CommentVoteType, VoteCommentRequest } from '@app/_proto/Protos/comments';
import { CommentsClient } from '@app/_proto/Protos/comments.client';
import { ReactComponent as GoodIcon } from '@app/assets/good.svg';
import { ReactComponent as MoreVert } from '@app/assets/more-vert.svg';
import { ReactComponent as WarningIcon } from '@app/assets/warning-old.svg';
import AuthorizationView from '@app/components/auth.view';
import CommentSubmit from '@app/components/entity-comments/comment.submit';
import LazyAvatar from '@app/components/lazy-avatar';
import { ResponsiveMenu, ResponsiveMenuItem } from '@app/components/responsive-menu';
import UserBadge from '@app/components/user.badge';
import ChampionAvatarFrame from '@app/domains/champion/components/ChampionAvatarFrame';
import TruncateDisclosure from '@app/domains/common/components/TruncateDisclosure';
import TruncateShowOrHide from '@app/domains/common/components/TruncateShowOrHide';
import type { ClassNameType } from '@app/domains/common/types';
import UserFlairBadge from '@app/domains/user/components/UserFlairBadge';
import { useAuth } from '@app/libs/auth';
import { GrpcError, getOneofValue } from '@app/libs/grpc';
import { useHttp } from '@app/libs/http';
import { batch, timestampToUnix } from '@app/libs/utils';
import { breakpoints } from '@app/utils/breakpoints';
import { paragraphTransform, useHtmlToReact } from '@app/utils/html';
import { useTimeAgo } from '@app/utils/time';
import { isBrowser } from '@app/utils/utils';
import { IconButton, useMediaQuery } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import animateScrollTo from 'animated-scroll-to';
import clsx from 'clsx';
import dayjs from 'dayjs';
import {
    type ButtonHTMLAttributes,
    type ComponentType,
    type HTMLAttributes,
    type PropsWithChildren,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { ReactComponent as Paragraph } from 'remixicon/icons/Editor/paragraph.svg';
import { twMerge } from 'tailwind-merge';
import { useImmer } from 'use-immer';

type Props = {
    entityId: number;
    comment: CommentItem;
    parentComment?: CommentItem;
    paragraphId?: string;
    visible?: boolean;
    replyDisabled?: boolean;
    replyDisabledMessage?: string;
    onReplySubmitted?: (id: number) => void;
    onReportComment?: (comment: CommentItem) => void;
} & HTMLAttributes<HTMLDivElement>;

export default function CommentCard({
    comment: initialComment,
    entityId,
    parentComment,
    paragraphId,
    visible,
    replyDisabled,
    replyDisabledMessage,
    onReplySubmitted,
    onReportComment,
    ...rest
}: Props) {
    //#region : Variables, functions and api calls
    const queryClient = useQueryClient();
    const isMobile = useMediaQuery(breakpoints.downSm2, {
        noSsr: true,
    });
    //General
    const [comment, setComment] = useImmer(initialComment);

    const commentParagraphId = useMemo(() => {
        if (comment.entityInfo?.info.oneofKind !== 'chaptersInfo') {
            return false;
        }

        if (!isBrowser()) {
            return false;
        }

        return comment.entityInfo.info.chaptersInfo?.paragraph?.id;
    }, [comment?.entityInfo]);

    // const { hidden } = parseCommentStatus(comment);

    const commentContent = useHtmlToReact(comment.content?.value || '', {
        transform: {
            p: paragraphTransform,
        },
    });

    //Refresh the comment state when initialComment data changes.
    useEffect(() => {
        setComment(initialComment);
    }, [initialComment, setComment]);

    //#region : Comment post time and edit time refresh
    const { user, login } = useAuth();
    const timeAgo = useTimeAgo(timestampToUnix(comment.timeCommented), {
        refreshInterval: comment.poster && comment.poster.id === user?.id ? 1000 * 60 : 0,
    });
    const [isRecent, setIsRecent] = useState(false);

    //User can edit his comment only for 5 minutes after posting it.
    //The following section simply sets a timer to disable edit 5 minutes after comment post time
    useEffect(() => {
        if (!!user && comment.poster?.id === user.id) {
            //To store the timeout function reference
            let timeoutHandle: number | NodeJS.Timeout | null = null;

            const commentTime = dayjs.unix(timestampToUnix(comment.timeCommented)!);
            const diff = commentTime.add(5, 'minute').diff(dayjs());

            if (diff > 0) {
                setIsRecent(true);

                timeoutHandle = setTimeout(() => {
                    clearTimeout(timeoutHandle as any);
                    setIsRecent(false);
                }, diff);
            } else {
                setIsRecent(false);
            }

            //Clear the timeout if the page is unloaded for any reason
            return () => {
                if (timeoutHandle) {
                    clearTimeout(timeoutHandle as any);
                }
            };
        }
    }, [comment.poster?.id, comment.timeCommented, user]);
    //#endregion : Comment post time and edit time refresh

    const { grpcRequest } = useHttp();

    const voteCommentAsync = useCallback(
        async (upVote: boolean) => {
            const type = upVote ? CommentVoteType.UpVote : CommentVoteType.DownVote;

            const request = VoteCommentRequest.create({
                id: comment.id,
                type,
            });

            try {
                const response = await grpcRequest(CommentsClient, c => c.voteComment, request);

                if (response.result) {
                    queryClient.invalidateQueries(['comments']);
                }
            } catch (e) {
                console.error(e);

                if (e instanceof GrpcError) {
                    console.error(e.response?.description);
                }
            }
        },
        [comment.id, grpcRequest, queryClient]
    );

    const onVoteUp = useCallback(() => {
        if (!user) {
            login(window.location.pathname);

            return;
        }

        voteCommentAsync(true);
    }, [login, user, voteCommentAsync]);

    const onVoteDown = useCallback(() => {
        if (!user) {
            login(window.location.pathname);

            return;
        }

        voteCommentAsync(false);
    }, [login, user, voteCommentAsync]);
    //#endregion : Vote action handlers

    //#region : Comment reply, edit and report action handlers
    const [submitComment, setSubmitComment] = useState(false);
    const [isEdit, setIsEdit] = useState(false);

    const onReply = useCallback(() => {
        batch(() => {
            setSubmitComment(prev => !prev);
            setIsEdit(false);
        });
    }, []);

    const onEdit = useCallback(() => {
        batch(() => {
            setIsEdit(prev => {
                const newIsEdit = !prev;

                setSubmitComment(newIsEdit);

                return newIsEdit;
            });
        });
    }, []);

    /**
     * Custom. Called by CommentSubmit component when reply/edit is completed successfully
     */
    const onCommentSubmitted = useCallback(
        (id: number) => {
            setSubmitComment(false);

            onReplySubmitted?.(id);
        },
        [onReplySubmitted]
    );

    const onReport = useCallback(() => {
        onReportComment?.(comment);
    }, [comment, onReportComment]);
    //#endregion : Comment reply, edit and report action handlers

    const handleGoToParagraph = () => {
        const el = document.querySelector(`[data-uid="${commentParagraphId}"]`) as HTMLElement;

        if (commentParagraphId && el) {
            el.classList.add('p-highlight-bg');

            setTimeout(() => {
                if (el) {
                    el.classList.remove('p-highlight-bg');
                }
            }, 1000);

            animateScrollTo(el, {
                cancelOnUserAction: false,
                verticalOffset: -100,
                maxDuration: 500,
            });
        }
    };

    //#endregion : Variables, functions and api calls

    const isChampionedCommentUser = getOneofValue(comment.posterEntityInfo?.entityInfo, 'seriesInfo')?.isChampion;

    // if (hidden) {
    //     if (hidden.reason === CommentStatus_HiddenReason.Reporter) {
    //         return (
    //             <ReportedCommentCard
    //                 className={className}
    //                 reportedReason="This comment is not visible only to you because you reported it."
    //             />
    //         );
    //     }
    //     if (hidden.reason === CommentStatus_HiddenReason.Reported) {
    //         return (
    //             <ReportedCommentCard
    //                 className={className}
    //                 reportedReason="This comment has been hidden due to a report."
    //             />
    //         );
    //     }
    // }

    return (
        <CardContainer {...rest}>
            <div>
                <div className="flex items-start justify-start space-x-6 sm:space-x-8">
                    <div className="mt-0">
                        {isChampionedCommentUser ? (
                            <ChampionAvatarFrame
                                frameWidth={isMobile ? 2 : 3}
                                classes={{ badge: 'w-30 h-14 sm2:w-34 sm2:h-16' }}
                            >
                                <LazyAvatar
                                    src={comment.poster?.avatarUrl?.value}
                                    className="h-36 w-36 border-none sm2:h-54 sm2:w-54"
                                    imageRenderSize={54}
                                    variant="rounded"
                                />
                            </ChampionAvatarFrame>
                        ) : (
                            <LazyAvatar
                                src={comment.poster?.avatarUrl?.value}
                                className="h-[40px] w-[40px] rounded-[6px] border-none sm:h-[60px] sm:w-[60px]"
                                imageRenderSize={60}
                                variant="rounded"
                            />
                        )}
                    </div>
                    <div className="flex-1">
                        <div>
                            <div className="flex flex-row flex-nowrap items-center">
                                <span className="font-set-b15 text-gray-t1 break-word line-clamp-1 sm:font-set-b16">
                                    {comment.poster?.userName || '[Unknown Account]'}
                                </span>

                                {!!comment.poster?.flair?.value && (
                                    <UserFlairBadge flair={comment.poster.flair.value} />
                                )}
                                {!!comment.poster?.isVipActive && (
                                    <div className="ml-2 sm2:ml-4">
                                        <UserBadge badgeType="vip" vipType={comment.poster?.vip?.type} />
                                    </div>
                                )}
                            </div>
                            <div className="font-set-r13 flex text-gray-600 sm:font-set-r14">
                                <div>{timeAgo}</div>
                                {comment.edited && (
                                    <>
                                        <span className="mx-4">&middot;</span>
                                        <div>Edited</div>
                                    </>
                                )}
                            </div>
                        </div>
                        <div className="ml-[-46px] mt-[14px] break-word sm:ml-0 sm:mt-[8px]">
                            <ContentContainer>
                                <TruncateDisclosure>
                                    <TruncateDisclosure.Content lines={6} className="whitespace-pre-wrap text-gray-t1">
                                        {!!parentComment && (
                                            <span className="text-[14px] font-bold break-word sm:text-[16px]">
                                                @{parentComment.poster?.userName || '[Unknown Account]'}&nbsp;
                                            </span>
                                        )}
                                        {commentContent}
                                    </TruncateDisclosure.Content>
                                    {!!visible && (
                                        <TruncateDisclosure.Toggle
                                            className="font-set-m12 mb-2 mt-6"
                                            showingComponent={<TruncateShowOrHide showing={true} />}
                                            hidingComponent={<TruncateShowOrHide showing={false} />}
                                        />
                                    )}
                                </TruncateDisclosure>
                            </ContentContainer>
                            <div className="flex text-[#616161] dark:text-[#BDBDBD]">
                                <div className="flex-1 space-x-[16px]">
                                    <div className="flex">
                                        <AuthorizationView>
                                            {!!user && comment.poster?.id === user.id && isRecent && (
                                                <div className="px-6 pb-9 pt-6 sm:px-4 sm:pt-8">
                                                    <button
                                                        className="flex text-gray-t3 with-hover:hover:text-blue-600 dark:with-hover:hover:text-blue-600"
                                                        onClick={onEdit}
                                                    >
                                                        <span className="font-set-m14 sm:font-set-m16">Edit</span>
                                                    </button>
                                                </div>
                                            )}
                                        </AuthorizationView>
                                        <div className="ml-6 px-6 pb-9 pt-6 sm:ml-8 sm:px-4 sm:pt-8">
                                            <CountWithIconButton
                                                count={comment.upVotes}
                                                onClick={onVoteUp}
                                                IconComponent={GoodIcon}
                                            />
                                        </div>
                                        <div className="ml-6 px-6 pb-9 pt-6 sm:ml-8 sm:px-4 sm:pt-8">
                                            <CountWithIconButton
                                                classes={{ icon: 'rotate-180' }}
                                                count={comment.downVotes}
                                                onClick={onVoteDown}
                                                IconComponent={GoodIcon}
                                            />
                                        </div>
                                        {!paragraphId &&
                                            comment.entityInfo?.info.oneofKind === 'chaptersInfo' &&
                                            !!comment.entityInfo.info.chaptersInfo.paragraph && (
                                                <div className="ml-6 px-6 pb-9 pt-6 sm:ml-8 sm:px-4 sm:pt-8">
                                                    <button
                                                        className="flex text-gray-t3 with-hover:hover:text-blue-600 dark:with-hover:hover:text-blue-600"
                                                        onClick={handleGoToParagraph}
                                                    >
                                                        <Paragraph className="h-16 w-16 fill-current sm:h-18 sm:w-18" />
                                                    </button>
                                                </div>
                                            )}
                                        <AuthorizationView>
                                            <div className="ml-6 px-6 pb-9 pt-6 sm:ml-8 sm:px-4 sm:pt-8">
                                                <button
                                                    className="flex text-gray-t3 with-hover:hover:text-blue-600 dark:with-hover:hover:text-blue-600"
                                                    onClick={onReply}
                                                >
                                                    <span className="font-set-m14 sm:font-set-m16">Reply</span>
                                                </button>
                                            </div>
                                        </AuthorizationView>
                                    </div>
                                </div>
                                <div>
                                    <AuthorizationView>
                                        <ResponsiveMenu
                                            menuType="hybrid"
                                            MenuOpenerComponent={
                                                <IconButton
                                                    className="w-[20px] p-0 text-inherit hover:bg-transparent hover:text-blue"
                                                    size="large"
                                                    disableRipple
                                                >
                                                    <MoreVert />
                                                </IconButton>
                                            }
                                        >
                                            <ResponsiveMenuItem
                                                onClick={onReport}
                                                icon={<WarningIcon className="" />}
                                                text="Report"
                                            />
                                        </ResponsiveMenu>
                                    </AuthorizationView>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {submitComment && (
                <div className="my-[15px]">
                    <CommentSubmit
                        entityId={entityId}
                        isEdit={isEdit}
                        disabled={replyDisabled}
                        disabledMessage={replyDisabledMessage}
                        parentComment={comment}
                        onCommentSubmitted={onCommentSubmitted}
                        paragraphId={paragraphId}
                    />
                </div>
            )}
        </CardContainer>
    );
}

const ContentContainer = ({ children }: PropsWithChildren<{}>) => {
    return <div className="rounded-12 bg-gray-container-base px-12 py-8">{children}</div>;
};

const CardContainer = ({ children, className, ...props }: HTMLAttributes<HTMLDivElement>) => {
    return (
        <div className={twMerge('py-8', className)} {...props}>
            {children}
        </div>
    );
};

type CountWithIconButtonProps = {
    IconComponent: ComponentType<ClassNameType>;
    classes?: { icon?: string };
    count: number;
} & ButtonHTMLAttributes<HTMLButtonElement>;
const CountWithIconButton = ({ IconComponent, classes, count, disabled, ...props }: CountWithIconButtonProps) => {
    return (
        <button
            className={clsx(
                'flex text-gray-t3',
                !disabled ? 'with-hover:hover:text-blue-600 dark:with-hover:hover:text-blue-600' : 'cursor-auto'
            )}
            onClick={e => {
                if (disabled) e.stopPropagation();
            }}
            {...props}
        >
            <IconComponent className={clsx('h-16 w-16 sm:h-18 sm:w-18', classes?.icon)} />
            <span className="font-set-m14 pl-4 sm:font-set-m16">{count}</span>
        </button>
    );
};

// const ReportedCommentCard = ({ reportedReason, className }: { reportedReason: string } & ClassNameType) => {
//     return (
//         <CardContainer className={className}>
//             <div className="sm:ml-68 sm:mb-8">
//                 <ContentContainer>
//                     <span className="text-gray-desc">{reportedReason}</span>
//                 </ContentContainer>
//             </div>
//         </CardContainer>
//     );
// };
